import { FC, useId, useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUIStore, CURRENCIES } from '@/stores';
import { roundTo } from '@/shared/lib/number';
import { NumberInput } from '@/shared/ui/NumberInput';
import { ExportPDFButton } from '@/features/pdf-export';
import { ContractGeneratorButton, ActEditor } from '@/features/contracts';
import { computeEstimateTotals } from '@/entities/estimate/lib/computeTotals';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import type { Room } from '@/entities/room/model/types';
import type { EstimateItem, ProjectMeta } from '@/entities/estimate/model/types';
import type { BOMTotalsResult } from '@/entities/material';

// ---------------------------------------------------------------------------
// MaterialsToggle — enable/disable Materials tab (MAT-04, off by default)
// ---------------------------------------------------------------------------

const MaterialsToggle: FC = () => {
  const { t } = useTranslation();
  const materialsTabEnabled = useUIStore((s) => s.materialsTabEnabled);
  const setMaterialsTabEnabled = useUIStore((s) => s.setMaterialsTabEnabled);

  return (
    <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm mt-2">
      <label className="flex items-center justify-between gap-2 cursor-pointer select-none">
        <div>
          <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase">
            {t('materials.enable_tab', 'Enable Materials tab')}
          </div>
          <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
            {t('materials.enable_tab_desc', 'Show material consumption list (BOM) for each room')}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={materialsTabEnabled}
          onClick={() => setMaterialsTabEnabled(!materialsTabEnabled)}
          className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${
            materialsTabEnabled
              ? 'bg-blue-600'
              : 'bg-slate-300 dark:bg-slate-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 mt-0.5 ${
              materialsTabEnabled ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
      </label>
    </div>
  );
};

// Маржа — глобальный регулятор сметы (в процентах). Допускает МИНУС, чтобы можно было
// «сбросить» итог ползунком, не переписывая всю базу цен (запрошено пользователем).
// MARGIN_MIN = -100: фактор `1 + margin/100` становится 0 → смета услуг обнуляется
//   (расчёт всё равно полит `Math.max(0, qty*factor)`), ниже -100 смысла нет.
// MARGIN_MAX = 400: прежний потолок наценки.
// MARGIN_STEP = 1: тонкий шаг под кнопки ±; ручной ввод в NumberInput допускает и 0.5.
const MARGIN_MIN = -100;
const MARGIN_MAX = 400;
const MARGIN_STEP = 1;

interface ProjectStatsProps {
    projectTotal: number;
    totalArea: number;
    safetyMargin: number;
    setSafetyMargin: (margin: number) => void;
    onDownloadExcel: () => void;
    rooms?: Room[];
    projectItems?: EstimateItem[];
    meta?: ProjectMeta;
    /** CR-01 (MAT-04): when materialsTabEnabled and the active room has material data,
     *  pass the pre-computed totals here so ExportPDFButton forwards them to the PDF renderer. */
    materialTotals?: BOMTotalsResult;
    /** TRUTH-02: optional project-level geometry override, forwarded to ExportPDFButton so the
     *  PDF prices global items identically to the screen. */
    projectOverrides?: { floorArea?: number; wallArea?: number; perimeter?: number };
}

export const ProjectStats: FC<ProjectStatsProps> = ({
    projectTotal,
    totalArea,
    safetyMargin,
    setSafetyMargin,
    onDownloadExcel,
    rooms = [],
    projectItems = [],
    meta = {},
    materialTotals,
    projectOverrides,
}) => {
    const { t } = useTranslation();
    const currency = useUIStore((s) => s.currency);
    const setCurrency = useUIStore((s) => s.setCurrency);
    const availableCurrencies = CURRENCIES;
    const marginId = useId();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [actEditorOpen, setActEditorOpen] = useState(false);

    // vatRate from project meta (CLAUDE.md rule 8: no hardcoded magic numbers — named fallback)
    const DEFAULT_VAT_RATE = 0.23;
    const vatRate = meta?.vatRate ?? DEFAULT_VAT_RATE;
    // estimateLines memoized — recomputed only when rooms/items/margin/vatRate change
    const estimateLines = useMemo(
      () => computeEstimateTotals(rooms, projectItems, safetyMargin, vatRate).lines,
      [rooms, projectItems, safetyMargin, vatRate],
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="p-5 bg-slate-50 dark:bg-slate-800/50">
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">
                {t('editor.sidebar.total_estimate', 'Итого смета')}
            </div>
            <div className="text-3xl font-extrabold font-mono tabular-nums text-slate-800 dark:text-slate-100 tracking-tight">
                <span className="font-mono tabular-nums" data-testid="project-total-value">{roundTo(projectTotal || 0, 0)}</span> <span className="text-lg text-slate-500 dark:text-slate-400 font-medium">{currency}</span>
            </div>
            
            <div className="mt-4 space-y-2">
                <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex justify-between items-center">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase">
                            {t('editor.sidebar.price_per_m2', 'Цена за м²')}
                        </div>
                        <div className="font-bold text-slate-700 dark:text-slate-200">
                            <span className="font-mono tabular-nums">{roundTo((totalArea > 0 ? (projectTotal / totalArea) : 0), 0)}</span>{' '}
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">{currency}</span>
                        </div>
                    </div>
                </div>
 
                <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm">
                    <label 
                        htmlFor={marginId}
                        className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase block cursor-pointer"
                    >
                        {t('editor.sidebar.margin', 'Маржа')}
                    </label>
                    <div className="flex items-center gap-1.5 mt-1">
                        <button
                            type="button"
                            onClick={() => setSafetyMargin(Math.max(MARGIN_MIN, roundTo((safetyMargin || 0) - MARGIN_STEP, 1)))}
                            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 active:scale-90 transition-all font-bold text-sm"
                            aria-label={t('common.decrease', 'Уменьшить')}
                        >
                            −
                        </button>
                        <NumberInput
                            id={marginId}
                            value={safetyMargin || 0}
                            onChange={(val) => setSafetyMargin(Math.min(MARGIN_MAX, Math.max(MARGIN_MIN, val)))}
                            placeholder="0"
                            className="w-full bg-transparent font-bold text-slate-700 dark:text-slate-200 outline-none text-sm text-center placeholder:text-slate-300 dark:placeholder:text-slate-600"
                        />
                        <button
                            type="button"
                            onClick={() => setSafetyMargin(Math.min(MARGIN_MAX, roundTo((safetyMargin || 0) + MARGIN_STEP, 1)))}
                            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-900/30 dark:hover:text-green-400 active:scale-90 transition-all font-bold text-sm"
                            aria-label={t('common.increase', 'Увеличить')}
                        >
                            +
                        </button>
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 shrink-0">%</span>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm relative" ref={dropdownRef}>
                    <label 
                        className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase block cursor-pointer mb-1 select-none"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                        {t('common.currency', 'Валюта')}
                    </label>
                    <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="w-full flex items-center justify-between font-bold text-slate-700 dark:text-slate-200 text-sm mt-1 py-1 text-left"
                    >
                        <span>{availableCurrencies.find(c => c.code === currency)?.label || currency}</span>
                        <motion.div
                            animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ChevronDown size={16} className="text-slate-400" />
                        </motion.div>
                    </button>

                    <AnimatePresence>
                        {isDropdownOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.15 }}
                                className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl shadow-lg z-50 overflow-hidden"
                            >
                                {availableCurrencies.map(c => (
                                    <button
                                        key={c.code}
                                        type="button"
                                        onClick={() => {
                                            setCurrency(c.code);
                                            setIsDropdownOpen(false);
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-xs font-bold transition-colors flex items-center justify-between ${
                                            currency === c.code 
                                                ? 'bg-slate-50 dark:bg-slate-700/50 text-green-600 dark:text-green-400' 
                                                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/30'
                                        }`}
                                    >
                                        <span>{c.label}</span>
                                        {currency === c.code && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                                    </button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* MAT-04: Materials tab toggle — off by default (CLAUDE.md rule 10) */}
            <MaterialsToggle />

            <div className="mt-4 flex gap-2">
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onDownloadExcel}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl flex items-center justify-center gap-2 font-bold text-sm shadow-lg shadow-green-200 dark:shadow-none transition-colors"
                >
                    <Download size={18}/> {t('editor.sidebar.download_excel', 'Скачать Excel')}
                </motion.button>
                <ExportPDFButton
                    rooms={rooms}
                    projectItems={projectItems}
                    safetyMargin={safetyMargin}
                    meta={meta}
                    currency={currency}
                    materialTotals={materialTotals}
                    projectOverrides={projectOverrides}
                    className="shrink-0 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-xl font-bold text-sm shadow-lg shadow-red-200 dark:shadow-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                />
            </div>

            {/* DOC-01 / DOC-02: Contracts & Acts section — closes audit BLOCKER */}
            <div className="mt-4">
                <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">
                    {t('contract.section_title', 'Contracts & Acts')}
                </div>
                <div className="space-y-2">
                    {/* DOC-01: umowa PDF — gate-aware internally (DOC-03) */}
                    <ContractGeneratorButton
                        rooms={rooms}
                        projectItems={projectItems}
                        safetyMargin={safetyMargin}
                        meta={meta}
                        currency={currency}
                        className="w-full bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 text-white py-2.5 rounded-xl font-bold text-sm transition-colors"
                    />
                    {/* DOC-02: completion act editor trigger */}
                    <button
                        type="button"
                        onClick={() => setActEditorOpen(true)}
                        className="w-full bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 text-white py-2.5 rounded-xl font-bold text-sm transition-colors"
                    >
                        {t('contract.generate_act', 'Generate act')}
                    </button>
                </div>
            </div>

            {/* DOC-02: ActEditor modal — dismissible=false guards accidental data loss */}
            <ResponsiveModal
                open={actEditorOpen}
                onOpenChange={setActEditorOpen}
                title={t('contract.act_editor_title', 'Completion act')}
                dismissible={false}
                className="max-w-2xl p-0"
            >
                <ActEditor
                    estimateLines={estimateLines}
                    vatRate={vatRate}
                    currency={currency}
                    onActCreated={() => setActEditorOpen(false)}
                />
            </ResponsiveModal>
        </div>
    );
};
